import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import {
  collection, addDoc, onSnapshot, query, where, Timestamp, orderBy, doc, updateDoc
} from 'firebase/firestore';
import moment from 'moment';
import Swal from 'sweetalert2';

function Dailydenomination() {
  const [orderdate, setOrderdate] = useState(moment().format("YYYY-MM-DD"));
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [expectedCash, setExpectedCash] = useState(0);
  const [modes, setModes] = useState([]); 
  const [notes, setNotes] = useState({}); 

  const userId = auth.currentUser?.uid;
  const Toast = Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });

  // Effect 1: Fetch Payment Modes (Runs once on mount per user)
  useEffect(() => {
    if (!userId) return;

    const qModes = query(
      collection(db, "payment_modes"), 
      where("userId", "==", userId), 
      orderBy("createdAt", "desc")
    );
    
    const unsubModes = onSnapshot(qModes, (snap) => {
      const modeList = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setModes(modeList);
      
      // Seed initial key values if they don't exist yet
      setNotes(prev => {
        const updatedNotes = { ...prev };
        modeList.forEach(m => {
          if (updatedNotes[m.name] === undefined) {
            updatedNotes[m.name] = 0;
          }
        });
        return updatedNotes;
      });
    });

    return () => unsubModes();
  }, [userId]);

  // Effect 2: Fetch Expected Sales and Existing Tally (Re-runs smoothly whenever orderdate changes)
  useEffect(() => {
    if (!userId) return;

    // A. Fetch Expected Sales from daily_ledger for chosen date
    const salesQuery = query(
      collection(db, "daily_ledger"), 
      where("userId", "==", userId), 
      where("date", "==", orderdate)
    );
    
    const unsubSales = onSnapshot(salesQuery, (snap) => {
      let revenue = 0;
      snap.forEach(doc => {
        revenue += Number(doc.data().totalRevenue || 0);
      });
      setExpectedCash(revenue); 
    });

    // B. Auto-Fetch Existing Tally from denominations for chosen date
    const histQuery = query(
      collection(db, "denominations"), 
      where("userId", "==", userId), 
      where("date", "==", orderdate)
    );
    
    const unsubHist = onSnapshot(histQuery, (snap) => {
      if (!snap.empty) {
        const existingData = snap.docs[0].data();
        setEditId(snap.docs[0].id);
        setNotes(existingData.breakdown || {});
      } else {
        setEditId(null);
        // Reset inputs to 0 for a clean new entry on this date
        setNotes(prev => {
          const resetNotes = {};
          Object.keys(prev).forEach(key => {
            resetNotes[key] = 0;
          });
          return resetNotes;
        });
      }
    });

    return () => {
      unsubSales();
      unsubHist();
    };
  }, [orderdate, userId]);

  const handleInputChange = (val, name) => {
    setNotes(prev => ({ ...prev, [name]: parseFloat(val) || 0 }));
  };

  // CRITICAL CALCULATION LOGIC
  const calculateRowTotal = (m) => {
    const inputValue = notes[m.name] || 0;
    const denom = parseFloat(m.name);
    
    // If name is a number (e.g., 500, 200, 100), multiply input (Qty) by Denomination value
    if (!isNaN(denom)) {
      return denom * inputValue;
    }
    // If name is text (e.g., GPay, Card, PhonePe), input IS the direct total amount
    return inputValue;
  };

  const calculateGrandTotal = () => {
    return modes.reduce((acc, m) => acc + calculateRowTotal(m), 0);
  };

  const saveOrUpdate = async () => {
    const actualTotal = calculateGrandTotal();
    setLoading(true);
    
    const payload = {
      userId,
      date: orderdate,
      breakdown: notes,
      actualCash: actualTotal,
      expectedCash,
      difference: actualTotal - expectedCash,
      updatedAt: Timestamp.now()
    };

    try {
      if (editId) {
        await updateDoc(doc(db, "denominations", editId), payload);
        Toast.fire({ icon: 'success', title: "Tally Updated Successfully" });
      } else {
        await addDoc(collection(db, "denominations"), { ...payload, created: Timestamp.now() });
        Toast.fire({ icon: 'success', title: "Tally Saved Successfully" });
      }
    } catch (err) {
      Toast.fire({ icon: 'error', title: err.message });
    } finally {
      setLoading(false);
    }
  };

  const actualTotal = calculateGrandTotal();
  const diff = actualTotal - expectedCash;

  return (
    <div className="content-wrapper bg-white min-vh-100">
      <div className="container-fluid pt-2 px-2">
        
        {/* Header Section */}
        <div className="d-flex justify-content-between align-items-center bg-white p-3 shadow-sm rounded-lg border">
          <div>
            <h4 className="font-weight-bold text-navy mb-0">Collection Dashboard</h4>
            <span className={`badge badge-pill ${editId ? 'badge-warning text-dark' : 'badge-success'}`}>
              {editId ? 'Editing Saved Record' : 'New Entry'}
            </span>
          </div>
          <div className="d-flex align-items-center">
            <input 
              type="date" 
              className="form-control border-primary font-weight-bold" 
              value={orderdate} 
              onChange={e => setOrderdate(e.target.value)} 
              style={{ width: '200px' }} 
            />
          </div>
        </div>

        {/* Summary Stats */}
        <div className="row mt-2">
          <div className="col-md-4 mb-2">
            <div className="p-3 bg-success text-white rounded shadow-sm text-center">
              <small className="font-weight-bold text-uppercase d-block mb-1">Expected Sale</small>
              <h3 className="font-weight-bold mb-0">₹ {expectedCash.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h3>
            </div>
          </div>
          <div className="col-md-4 mb-2">
            <div className="p-3 bg-navy text-white rounded shadow-sm text-center">
              <small className="font-weight-bold text-uppercase d-block mb-1">Current Total</small>
              <h3 className="font-weight-bold mb-0">₹ {actualTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h3>
            </div>
          </div>
          <div className="col-md-4 mb-2">
            <div className={`p-3 rounded shadow-sm text-center text-white ${diff < 0 ? 'bg-danger' : 'bg-info'}`}>
              <small className="font-weight-bold text-uppercase d-block mb-1">Short / Excess</small>
              <h3 className="font-weight-bold mb-0">₹ {diff.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h3>
            </div>
          </div>
        </div>

        {/* Horizontal Entry Boxes */}
        <div className="mt-1 p-3 bg-light rounded-lg border shadow-inner">
          <div className="row">
            {modes.map((m) => {
              const isNote = !isNaN(parseFloat(m.name));
              const rowTotal = calculateRowTotal(m);
              return (
                <div className="col-md-3 col-lg-2 mb-3" key={m.id}>
                  <div className="card h-100 border-0 shadow-sm rounded overflow-hidden">
                    <div className={`p-2 text-center text-xs font-weight-bold text-white ${isNote ? 'bg-primary' : 'bg-info'}`}>
                      {isNote ? `₹${m.name} Note` : m.name}
                    </div>
                    <div className="p-2 bg-white d-flex flex-column justify-content-between h-100">
                      <input 
                        type="number" 
                        className="form-control text-center font-weight-bold bg-light"
                        placeholder={isNote ? "Count" : "Amount"}
                        value={notes[m.name] === 0 ? "" : notes[m.name] || ""}
                        onChange={(e) => handleInputChange(e.target.value, m.name)}
                      />
                      <div className="text-center mt-2 small font-weight-bold text-navy">
                        ₹ {rowTotal.toLocaleString('en-IN')}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Action Button */}
        <div className="py-3">
          <button onClick={saveOrUpdate} disabled={loading} className="btn btn-primary btn-block btn-lg shadow font-weight-bold">
            {loading ? 'Processing...' : editId ? "UPDATE TODAY'S COLLECTION" : 'SAVE COLLECTION'}
          </button>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .bg-navy { background-color: #001f3f !important; }
        .shadow-inner { box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.06); }
      `}} />
    </div>
  );
}

export default Dailydenomination;